/**
 * Alat permeabilitas — metodenya, angka yang dibaca metode itu, dan kalibrasinya.
 *
 * Sebangun dengan keempat alat sebelumnya, dan dipakai oleh dua layar yang
 * sama: rel parameter di ruang kerja dan tahap kelima di rel alur
 * deterministik.
 *
 * Yang khas pada tahap ini adalah bahwa tidak satu pun dari ketiga angkanya
 * pernah bisa dicapai dari aplikasi ini — bukan metodenya, bukan Swirr, bukan
 * C. Yang ada di rel hanyalah sebuah kotak centang di panel "Opsi" yang
 * berbunyi "Use core data as anchor", dan kotak itu tidak mengubah satu sampel
 * pun dari kurvanya: ia hanya memutuskan lampu tahap kelima menyala hijau atau
 * kuning. Panel yang menawarkan kalibrasi tanpa menawarkan korelasi yang
 * dikalibrasinya adalah panel yang menamai sebuah keputusan yang tidak pernah
 * diambil.
 */
import { useState } from "react";

import type { ParameterValues } from "../types";
import { ComputedInputs, sameParam } from "./CurveSource";
import { Button, Dropdown, Modal, NumberField, ParamCaption, Toggle } from "./ui";

/**
 * Cara permeabilitas dihitung.
 *
 * Petunjuknya menyebut apa yang menggerakkan kurvanya, sebab justru itu yang
 * membelah keempatnya menjadi dua keluarga: Timur dan Coates membaca satu
 * angka Swirr untuk seluruh sumur, jadi kurvanya semata-mata PHIE yang
 * dibengkokkan; Timur–Coates dan Morris–Biggs membaca kurva SW tahap empat
 * sampel demi sampel, jadi kurvanya juga membawa serta tiap keputusan tahap
 * itu. Urutannya urutan pilihan: Timur–Coates lebih dulu sebab ia bawaan
 * aplikasi ini.
 */
export const PERM_METHODS = [
  { value: "timur_coates", label: "Timur–Coates", hint: "SW curve" },
  { value: "timur", label: "Timur", hint: "fixed Swirr" },
  { value: "coates", label: "Coates", hint: "fixed Swirr" },
  { value: "morris_biggs", label: "Morris–Biggs", hint: "SW curve" },
];

/** Rumus tiap pilihan, ditulis sebagaimana ia dihitung di mesin. */
export const PERM_FORMULAS: Record<string, string> = {
  timur: "k = 8581 · PHIE^4.4 / Swirr²",
  coates: "k = C · PHIE⁴ · ((1 − Swirr)/Swirr)²",
  timur_coates: "k = C · PHIE⁴ · ((1 − SW)/SW)²",
  morris_biggs: "k = 62500 · PHIE⁶ / SW²",
};

/**
 * Apa yang menggerakkan tiap kurva, dan di selang mana ia berhenti berlaku.
 *
 * Bukan sejarah korelasinya: yang perlu dibaca sebelum memilih adalah di mana
 * angkanya masih bisa dipercaya. Keempatnya sepakat di zona hidrokarbon yang
 * sudah di saturasi tak tereduksi, dan berselisih habis-habisan di kaki air —
 * tempat kedua metode yang membaca kurva SW meluncur ke nol.
 */
export const PERM_NOTES: Record<string, string> = {
  timur:
    "Reads no SW curve at all: one Swirr for the whole well, so its curve is " +
    "PHIE bent and nothing else. The coefficient 8581 and the exponent 4.4 are " +
    "Timur's fit to Gulf Coast core rather than settings, so C is not read. It " +
    "still reads in the water leg — and that is exactly what to remember: it " +
    "does not know there is water there.",
  coates:
    "The Coates form with a fixed Swirr as the irreducible water volume. Like " +
    "Timur it reads no SW curve, but its C is open to be fitted against core — " +
    "the one number here that is actually meant to be calibrated.",
  timur_coates:
    "The Coates form driven by the stage-four SW curve rather than one Swirr. " +
    "In a hydrocarbon zone already at irreducible saturation it equals Coates; " +
    "in the transition zone and in the water leg SW approaches one, its free " +
    "fluid index shrinks, and k reads close to zero. Wet sand is not tight " +
    "sand: below the contact, read Timur or Coates.",
  morris_biggs:
    "The oil form (C = 250), with the SW curve standing in for Swirr. PHIE is " +
    "raised to the sixth power, so it is the sharpest of the four — two " +
    "porosity units of difference multiply k several times over. Its water-leg " +
    "warning is the same as Timur–Coates', and steeper.",
};

/** Kedua metode yang dijalankan kurva SW; Swirr bagi keduanya hanya cadangan. */
const DRIVEN_BY_SW = new Set(["timur_coates", "morris_biggs"]);

/** C hanya dibaca kedua bentuk Coates; Timur dan Morris–Biggs berkoefisien tetap. */
const READS_COEFF = new Set(["coates", "timur_coates"]);

/**
 * Metode beserta angka yang dibacanya, tanpa menyimpan apa pun sendiri.
 *
 * Swirr selalu muncul sebab keempat metodenya memang memakainya — tetapi tidak
 * dengan peran yang sama, dan label yang dipakainya menyebutkan peran itu.
 * Pada Timur dan Coates ia yang menentukan seluruh kurvanya; pada kedua metode
 * yang membaca kurva SW ia hanya menambal sampel yang saturasinya kosong.
 */
export function PermFields({
  params,
  onChange,
  disabled = false,
}: {
  params: ParameterValues;
  onChange: (
    key: keyof ParameterValues,
    value: number | string | boolean | Record<string, string>,
  ) => void;
  disabled?: boolean;
}) {
  const method = String(params.perm_method ?? "timur_coates");
  const drivenBySw = DRIVEN_BY_SW.has(method);
  return (
    <>
      <ParamCaption>Method</ParamCaption>
      <Dropdown
        value={method}
        options={PERM_METHODS}
        onChange={(value) => onChange("perm_method", value)}
        width="100%"
        disabled={disabled}
      />
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-4)" }}>
        {PERM_FORMULAS[method] ?? ""}
      </span>

      {/* Dua kolom juga ketika hanya Swirr yang muncul: kotaknya lalu tidak
          berubah lebar setiap kali metodenya berganti, dan yang berpindah
          hanyalah C yang datang dan pergi di sebelahnya. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <NumberField
          label={drivenBySw ? "Swirr (fallback)" : "Swirr"}
          value={Number(params.swirr)}
          step={0.01}
          min={0}
          max={1}
          onChange={(v) => onChange("swirr", v)}
          disabled={disabled}
        />
        {READS_COEFF.has(method) && (
          <NumberField
            label="C (coefficient)"
            value={Number(params.perm_coeff)}
            step={100}
            min={0}
            onChange={(v) => onChange("perm_coeff", v)}
            disabled={disabled}
          />
        )}
      </div>

      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
        {PERM_NOTES[method] ?? ""}
      </span>

      {/* Kotak ini tidak mengubah satu sampel pun dari kurvanya: tidak ada
          angka inti yang dibaca tahap ini, dan tidak ada yang dicocokkan
          otomatis. Yang diputuskannya adalah lampu tahap kelima — sebuah
          korelasi dari cekungan lain yang belum pernah dihadapkan ke satu plug
          pun adalah tebakan yang dicetak dalam milidarcy, dan lampu kuning
          adalah cara mengatakannya. */}
      <Toggle
        label="Calibrated against core data"
        checked={Boolean(params.core_anchored)}
        onChange={(v) => onChange("core_anchored", v)}
        disabled={disabled}
      />
      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
        Changes no curve — it lights stage 5 and records in its note that this result
        has been set against core outside the application.
      </span>
      <ComputedInputs
        rows={[
          { curve: "PHIE", from: "computed by stage 3 on this same run" },
          { curve: "SW", from: "computed by stage 4; falls back to Swirr where it is blank" },
        ]}
      />

    </>
  );
}

/**
 * Kunci yang boleh berangkat dari jendela ini; sisanya bukan urusan k.
 *
 * SW tidak ada di sini meskipun dua dari empat metodenya membacanya, dengan
 * alasan yang sama seperti PHIE pada jendela sebelumnya: saturasi air dipilih
 * di tahap empat, tempat ia pertama kali menentukan sesuatu. Dua jendela yang
 * sama-sama boleh mengubah SW adalah dua tahap yang bisa berselisih tentang
 * kurva yang sama.
 */
const PERM_KEYS: (keyof ParameterValues)[] = [
  "perm_method", "swirr", "perm_coeff", "core_anchored",
];

/**
 * Tahap permeabilitas sebagai jendelanya sendiri.
 *
 * Aturannya sama dengan keempat jendela sebelumnya: yang dikirim pulang
 * hanyalah yang benar-benar diubah, sebab tiap run dengan override menerbitkan
 * versi parameter set baru.
 */
export function PermDialog({
  params,
  busy = false,
  disabled = false,
  onClose,
  onRun,
}: {
  params: ParameterValues;
  busy?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onRun: (overrides: Partial<ParameterValues>) => void;
}) {
  const [draft, setDraft] = useState<Partial<ParameterValues>>({});
  const merged = { ...params, ...draft } as ParameterValues;
  const changed = Object.fromEntries(
    PERM_KEYS.filter((key) => key in draft && !sameParam(draft[key], params[key])).map(
      (key) => [key, draft[key]],
    ),
  ) as Partial<ParameterValues>;

  return (
    <Modal
      title="5 · Permeability"
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={disabled}
            onClick={() => onRun(changed)}
          >
            Compute permeability
          </Button>
        </>
      }
    >
      <PermFields
        params={merged}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        disabled={disabled || busy}
      />
      <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
        {"The whole deterministic workflow is run again. Neither the cutoffs " +
          "nor HCPV read the PERM curve — both decide on Vsh, PHIE and SW — so " +
          "net pay does not change with this choice; what changes is the PERM " +
          "curve and the mean k of each zone, averaged geometrically because k " +
          "spans several orders of magnitude."}
      </span>
    </Modal>
  );
}
